/* eslint-disable no-extra-boolean-cast */
import React, { useEffect } from 'react'
import useDataLoader from 'core/hooks/useDataLoader'
import { loadServiceCatalog } from 'k8s/components/apiAccess/endpoints/actions'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import DocumentMeta from 'core/components/DocumentMeta'
import Grid from 'core/elements/grid'
import CodeBlock from 'core/components/CodeBlock'
import { useSelector } from 'react-redux'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { RootState } from 'app/store'
import { prop } from 'ramda'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Cookies from 'js-cookie'
import { DOMAIN_NAME } from 'app/constants'
import ExternalLink from 'core/components/ExternalLink'
import CopyToClipboard from 'core/components/CopyToClipboard'

const columns = [
  {
    key: 'type',
    label: 'Services',
    CellComponent: ({ value }) => <Text variant="caption1">{value}</Text>,
  },
  {
    key: 'url',
    label: 'URL',
    CellComponent: ({ value }) =>
      value?.split(',').map((url) => (
        <>
          <ExternalLink url={url.trim()}>{url.trim()}</ExternalLink>
          <br />
        </>
      )),
  },
]

export default function ApiServicesPage() {
  const classes = useStyles()
  const hiddenServices = [
    'cloudformation',
    'regioninfo',
    'gnocchi',
    'ceilometer',
    'orchestration',
    'instance-ha',
  ]
  const handleServiceDifferently = ['instance-ha', 'volumev3', 'orchestration', 'compute']

  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const {
    userDetails: { name },
    activeRegion,
    domain,
  } = session
  const { prefs } = useScopedPreferences()
  const { currentTenantName, currentRegion } = prefs
  const fqdn = window.location.host
  const currentDomain = domain?.name || Cookies.get(DOMAIN_NAME) || 'default'

  const [serviceCatalog, loading, loadData] = useDataLoader(loadServiceCatalog)

  // Load the service catalog on component mount to get fresh data
  useEffect(() => {
    loadData(true)
  }, [])

  const services = serviceCatalog
    .filter(
      (service) => !hiddenServices.includes(service.type) && !hiddenServices.includes(service.name),
    )
    .map((service) => {
      // For the 3 services, we need to remove the version and tenantId from the URL
      // Refer JIRA for more details - https://platform9.atlassian.net/browse/PCD-2054
      if (handleServiceDifferently.includes(service.type)) {
        return {
          ...service,
          url: `${service.url
            .split('/v')
            .slice(0, -1)
            .join('/v')}/`,
        }
      }
      return service
    })

  return (
    <>
      <DocumentMeta title="API Services" breadcrumbs />
      <div className={classes.sections}>
        <div className={classes.section}>
          <Text variant="subtitle1">API Endpoints</Text>
          <Grid
            uniqueIdentifier="id"
            data={services}
            columns={columns}
            loading={loading}
            disableToolbar
          />
        </div>
        <div className={classes.section}>
          <Text variant="subtitle1">pcdctl RC</Text>
          <Text variant="body2">
            These environment variables are needed to use the pcdctl command-line interfaces.
          </Text>
          <CopyToClipboard
            copyText={`export OS_USERNAME=${name}
export OS_PASSWORD=<your-password>
export OS_AUTH_URL=https://${fqdn}/keystone/v3
export OS_AUTH_TYPE=password
export OS_IDENTITY_API_VERSION=3
export OS_REGION_NAME=${currentRegion || activeRegion}
export OS_USER_DOMAIN_NAME=${currentDomain}
export OS_PROJECT_DOMAIN_NAME=${currentDomain}
export OS_PROJECT_NAME=${currentTenantName}
export OS_INTERFACE=public`}
          >
            <CodeBlock>
              {`export OS_USERNAME=${name}
export OS_PASSWORD=<your-password>
export OS_AUTH_URL=https://${fqdn}/keystone/v3
export OS_AUTH_TYPE=password
export OS_IDENTITY_API_VERSION=3
export OS_REGION_NAME=${currentRegion || activeRegion}
export OS_USER_DOMAIN_NAME=${currentDomain}
export OS_PROJECT_DOMAIN_NAME=${currentDomain}
export OS_PROJECT_NAME=${currentTenantName}
export OS_INTERFACE=public`}
            </CodeBlock>
          </CopyToClipboard>
        </div>
      </div>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  sections: {
    display: 'grid',
    gap: 32,
  },
  section: {
    display: 'grid',
    gap: 16,
  },
}))
